import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Input,
  Option,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import { type ClientInvoice } from '../../../types';

export interface SharedInvoicePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sharedInvoice: ClientInvoice | null | undefined;
  recordSharedPaymentMutation: UseMutationResult<unknown, Error, { invoiceId: string; amount: number; paymentType: 'check' | 'cash'; reference?: string }>;
}

export function SharedInvoicePaymentDialog({
  open,
  onOpenChange,
  sharedInvoice,
  recordSharedPaymentMutation,
}: SharedInvoicePaymentDialogProps) {
  const [sharedPaymentAmount, setSharedPaymentAmount] = useState('');
  const [sharedPaymentType, setSharedPaymentType] = useState<'check' | 'cash'>('cash');
  const [sharedPaymentReference, setSharedPaymentReference] = useState('');

  const handleCancel = () => {
    onOpenChange(false);
    setSharedPaymentAmount('');
    setSharedPaymentReference('');
  };

  const handleSubmit = () => {
    const amount = parseFloat(sharedPaymentAmount);
    if (amount > 0 && sharedInvoice) {
      recordSharedPaymentMutation.mutate(
        {
          invoiceId: sharedInvoice.id,
          amount,
          paymentType: sharedPaymentType,
          reference: sharedPaymentReference || undefined,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setSharedPaymentAmount('');
            setSharedPaymentReference('');
          },
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Record Payment on Shared Invoice</DialogTitle>
          <DialogContent>
            {sharedInvoice && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                  Invoice #{sharedInvoice.invoiceNumber} • Total: ${sharedInvoice.totalAmount.toFixed(2)} • Due: ${sharedInvoice.amountDue.toFixed(2)}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Payment Type">
                <Dropdown
                  value={sharedPaymentType === 'check' ? 'Check (Regions Bank)' : 'Cash'}
                  selectedOptions={[sharedPaymentType]}
                  onOptionSelect={(_, data) => setSharedPaymentType(data.optionValue as 'check' | 'cash')}
                >
                  <Option value="check">Check (Regions Bank - 9289)</Option>
                  <Option value="cash">Cash</Option>
                </Dropdown>
              </Field>
              <Field label="Amount" required>
                <Input
                  type="number"
                  value={sharedPaymentAmount}
                  onChange={(_, data) => setSharedPaymentAmount(data.value)}
                  placeholder="0.00"
                  contentBefore={<span>$</span>}
                />
              </Field>
              {sharedPaymentType === 'check' && (
                <Field label="Check Number (Reference)">
                  <Input
                    value={sharedPaymentReference}
                    onChange={(_, data) => setSharedPaymentReference(data.value)}
                    placeholder="e.g., 1234"
                  />
                </Field>
              )}
            </div>
            {recordSharedPaymentMutation.isError && (
              <div style={{ color: '#d83b01', fontSize: '13px', marginTop: '8px' }}>
                <WarningRegular /> {(recordSharedPaymentMutation.error as Error)?.message || 'Failed to record payment'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!sharedPaymentAmount || parseFloat(sharedPaymentAmount) <= 0 || recordSharedPaymentMutation.isPending || !sharedInvoice}
              onClick={handleSubmit}
            >
              {recordSharedPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
